import { Component, OnInit, ViewChild, ViewContainerRef, AfterViewInit, NgZone } from '@angular/core';
import { AccessToken } from '@okta/okta-auth-js';
import { Router, NavigationEnd, NavigationStart, RouterOutlet } from '@angular/router';
import { DeepLinkService } from '../services/deep-link.service';
import { DemoModeService } from '../services/demo-mode.service';
import { UserService } from '../services/user-service.service';
import { OktaAuthService } from '../services/okta-auth.service';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { Network } from '@capacitor/network';
import { Keyboard } from '@capacitor/keyboard';
import { UpdateCheckService } from '../services/mobile-app-update-check-service';
import { BottomSheetService } from './apps/shared/bottom-sheet/bottom-sheet.service';
import { HybridStorageKeys, HybridStorageService } from './services/hybrid-storage.service';
import { combineLatest, filter, take } from 'rxjs';
import { SystemUIService } from '../services/system-ui.service';
import { I18nConstantsComponent } from './i18n-constants.component';
import { CapgoReadyService } from '../services/capgo-ready.service';
import { cleanupDeviceFiles } from '../helpers/native-file.helper';
import { startTrustDiagnostics } from '../services/trust-diagnostics';
import { SchoolsService } from './apps/shared/schools.service';
import { MatDialog } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AnalyticsService } from './apps/analytics/services/analytics.service';
import { BadgeService } from './services/badge.service';
import { PushNotificationService } from '../services/push-notification-service';
import { GlobalPollingService } from '../services/global-polling.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NetworkMonitorService } from '../services/network-monitor.service';
import { LoginDebugService } from '../services/login-debug.service';
import { SystemNoticeService } from '../services/system-notice.service';
import { LoginDiagnosticsModalComponent, LoginDiagnostics } from './apps/shared/login-diagnostics-modal/login-diagnostics-modal.component';
import { Device } from '@capacitor/device';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css'],
    imports: [RouterOutlet, I18nConstantsComponent, MatProgressSpinnerModule]
})
export class AppComponent implements OnInit, AfterViewInit {
  @ViewChild('bottomSheetContainer', { read: ViewContainerRef }) bottomSheetContainer!: ViewContainerRef;
  @ViewChild('aiWidgetContainer', { read: ViewContainerRef }) aiWidgetContainer!: ViewContainerRef;
  userId: number | null = null;
  private isCriticalUpdateAvaliable = false;
  isDemoMode = false;
  private authReady = false;
  private navReady = false;
  private aiWidgetCreated = false;
  private isCreatingWidget = false;
  isAuthenticating = false; // Show loading overlay during cold start authentication

  // Mobile only: shown when cold-start token refresh fails because the network is unreachable
  // but we still hold a (valid) refresh token. Lets the user retry explicitly instead of being
  // dropped on the login screen, and is auto-retried when connectivity returns.
  authOfflineRetry = false;
  // Mobile only: shown instead of the offline/retry screen when Okta rejected the stored session
  // during the cold-start refresh. Retry can't succeed then, so the user is told to sign in again.
  authSessionExpired = false;
  isSigningInAgain = false;
  // Context captured at cold start so the reconnect listener / manual retry can re-run navigation.
  private coldStartInitialPath = '';
  private coldStartHasOktaRedirect = false;
  // Hard ceiling for the mobile cold-start refresh attempt before we surface the offline state.
  private static readonly MOBILE_COLD_START_CEILING_MS = 20000;

  constructor(
    private oktaAuthService: OktaAuthService,
    private deepLinkService: DeepLinkService,
    private demoModeService: DemoModeService,
    private userService: UserService,
    private updateCheckService: UpdateCheckService,
    private bottomSheetService: BottomSheetService,
    private hybridStorageService: HybridStorageService,
    private router: Router,
    private systemUIService: SystemUIService, // Initialize to detect and apply system font scale
    private capgoReady: CapgoReadyService,
    private schoolsService: SchoolsService,
    private zone: NgZone,
    private matDialog: MatDialog,
    private analyticsService: AnalyticsService,
    private badgeService: BadgeService,
    private pushNotificationService: PushNotificationService,
    private globalPollingService: GlobalPollingService,
    private networkMonitor: NetworkMonitorService,
    private loginDebugService: LoginDebugService,
    private snackBar: MatSnackBar,
    private systemNoticeService: SystemNoticeService // Initialize to show any active system notices on load
    ) {
      this.initCapgo();
      this.scheduleDeviceFileCleanup();
      this.setupGlobalModalClose();
      this.setupPhoneClickTracking();
      this.setupSwipeDownToDismissKeyboard();
      this.setupSwipeBackNavigation();
    }

  /**
   * Prunes temporary copies of opened attachments (and, on iOS, files older versions left in
   * Documents). Delayed so the disk work doesn't compete with cold-start auth.
   */
  private scheduleDeviceFileCleanup(): void {
    if (!Capacitor.isNativePlatform()) return;
    setTimeout(() => void cleanupDeviceFiles(), 30_000);
  }

  async ngOnInit() {
    // TEMPORARY: on-screen native TLS/HTTP diagnostics. No-ops on web and when TRUST_DIAG_OVERLAY
    // is false. Remove the call (or flip the flag) before shipping to users.
    startTrustDiagnostics();

    // Public guest surface (/guest/...): these visitors have no Okta session by design. Skip the
    // entire cold-start auth/deep-link flow so it never shows the auth overlay or bounces them to
    // login/mainmenu. The GuestModule + Guest JWT handle everything under this route on their own.
    if (window.location.pathname.startsWith('/guest')) {
      this.isAuthenticating = false;
      return;
    }

    // Show loading overlay during cold start authentication (mobile and web)
    this.isAuthenticating = true;

    // Capture the loaded path synchronously before any awaits — OktaAuthGuard can call
    // router.navigateByUrl('') which changes window.location.pathname to '/' during the
    // async operations below, making a late read unreliable.
    //
    // Capture it on NATIVE too (previously hardcoded ''): the native layer opens a deep link by
    // loading the webview at capacitor://localhost/<deep route>, so on a deep-link launch this is
    // the real target (e.g. /apps/posts/detail/123). If the cold-start auth flow runs before the
    // router has finished activating that route (the OktaAuthGuard is async), navigateAfterColdStartAuth
    // would otherwise see an auth route + an empty initial path and bounce the user to /mainmenu,
    // dropping the deep link. With the real path captured here it restores the deep route instead.
    // Safe across platforms: web already used exactly this value; on native it's only ever READ when
    // the router is still at an auth route, and a normal cold start yields '/' (→ /mainmenu, unchanged).
    const initialBrowserPath = window.location.pathname + window.location.search;

    // Mobile: the native layer opens deep links by re-loading the webview at
    // capacitor://localhost/s/<code> — on BOTH cold start AND warm resume (a resume re-loads the
    // webview, so this ngOnInit runs again). On resume App.getLaunchUrl() still returns the
    // ORIGINAL process-launch URL, so detecting the short link from getLaunchUrl alone misses
    // resume and we race DeepLinkService to /mainmenu (and win, since /mainmenu needs no network).
    // window.location.pathname — read here synchronously, before the wildcard route can rewrite it
    // — is authoritative in both cases. Flag it so the cold-start auth flow and HomeComponent stand
    // down; DeepLinkService (driven by the forcedAppUrlOpen event) resolves + navigates.
    // Match the trailing /s/<code>: iOS loads the webview at /s/<code>, Android prefixes it to
    // /apps/s/<code> (see MainActivity.onNewIntent). Anchoring on the END (no leading ^) catches
    // both forms — iOS still matches exactly as before, Android is simply added.
    if (Capacitor.isNativePlatform() && /\/s\/[A-Za-z0-9]{6}$/.test(window.location.pathname)) {
      this.deepLinkService.markDeepLinkPending();
    }

    // Failsafe so the overlay can never be stuck up forever.
    // Web: 10s (unchanged). Mobile: 25s backstop only — the mobile cold-start flow owns the
    // overlay and resolves it deterministically (success / offline-retry) within its own 20s
    // hard ceiling; this longer timer only fires if that flow throws unexpectedly.
    const overlayFailsafeMs = Capacitor.isNativePlatform() ? 25000 : 10000;
    setTimeout(() => {
      if (this.isAuthenticating) {
        console.warn(`[AppComponent] Auth timeout - forcing overlay to hide after ${overlayFailsafeMs}ms`);
        this.zone.run(() => { this.isAuthenticating = false; });
      }
    }, overlayFailsafeMs);

    // Subscribe to demo mode changes
    this.demoModeService.isDemoMode$.subscribe(isDemoMode => {
      this.isDemoMode = isDemoMode;

    });

    if (!Capacitor.isNativePlatform()) {
      const urlObj = new URL(window.location.href);
      let route = urlObj.pathname;
      if (urlObj.search) {
        route += urlObj.search;
      }

      // Don't store root, login, or callback paths as deep links
      const isAuthRoute = urlObj.pathname === '/' || route.includes('/login') || route.includes('/callback') || route.includes('/logout');
      if (!isAuthRoute) {
        // Store the route for after login
        await this.hybridStorageService.setItem(HybridStorageKeys.Capacitor_DeepLinkPath, route);
      } else if (!route.includes('/callback')) {
        // Clear any existing deep link when navigating to a deliberate auth route (login/logout/root)
        // Do NOT clear on /callback — Okta redirects us here and LoginCallbackComponent needs the stored deeplink
        await this.hybridStorageService.removeItem(HybridStorageKeys.Capacitor_DeepLinkPath);
      }
    }


    if (Capacitor.isNativePlatform()) {
      // When the keyboard dismisses, iOS leaves window.scrollY at the panned offset,
      // which shifts position:fixed pages up and hides the header. Reset it here globally.
      Keyboard.addListener('keyboardWillHide', () => {
        window.scrollTo(0, 0);
      });

      const launchUrl = await App.getLaunchUrl();
      if (launchUrl && launchUrl.url) {
        const urlObj = new URL(launchUrl.url);
        let route = urlObj.pathname;
        if (urlObj.search) {
          route += urlObj.search;
        }

        // Don't store root, login, or callback paths as deep links
        const isAuthRoute = urlObj.pathname === '/' || route.includes('/login') || route.includes('/callback') || route.includes('/logout');
        const isShortUrl = /^\/s\/[A-Za-z0-9]{6}$/.test(urlObj.pathname);
        if (isShortUrl) {
          // A short /s/<code> link can only be turned into a real route by resolving it
          // server-side, which DeepLinkService owns. Flag it now so the cold-start auth flow below
          // (navigateAfterColdStartAuth) doesn't race ahead to /mainmenu, and do NOT store the raw
          // '/s/<code>' — it isn't an Angular route, so navigating to it hits the wildcard and
          // bounces the user to /mainmenu. DeepLinkService resolves + navigates instead.
          this.deepLinkService.markDeepLinkPending();
        } else if (!isAuthRoute) {
          // Store the route for after login
          await this.hybridStorageService.setItem(HybridStorageKeys.Capacitor_DeepLinkPath, route);
        }
      }

      this.isCriticalUpdateAvaliable = (await this.updateCheckService.checkForUpdate()) ?? false;
    }

    // Only register appStateChange listener for native platforms (mobile)
    // On web, we don't need to refresh tokens when switching tabs
    if (Capacitor.isNativePlatform()) {
      App.addListener('appStateChange', async ({ isActive }) => {
        if (isActive) {
          // Don't reset critical update flags on app resume - only reset non-critical
          this.updateCheckService.hasShownNonCriticalUpdate = false;
          const criticalUpdateDetected = await this.updateCheckService.checkForUpdate();

          // If critical update is required, stop here - don't refresh tokens or navigate
          if (criticalUpdateDetected) {
            return;
          }
        // Clear badge when app becomes active (non-blocking, fire and forget)
        this.badgeService.clearBadgeAsync();
          // iOS: Sync tokens from Keychain (background BGTaskScheduler may have refreshed them)
          if (Capacitor.getPlatform() === 'ios') {
            const syncSuccess = await this.oktaAuthService.syncTokensFromKeychain();
            if (syncSuccess) {

              try {
                await this.userService.setUserProfile();
              } catch (error) {
                console.warn('[AppComponent] Failed to fetch user profile after iOS token sync on resume (possibly offline), using cached profile:', error);
                // Continue with cached profile from storage
              }

              return; // Skip normal refresh if we synced from Keychain
            }
          }

          // Android: Sync tokens from native storage (background WorkManager may have refreshed them)
          if (Capacitor.getPlatform() === 'android') {
            const syncSuccess = await this.oktaAuthService.syncTokensFromNativeStorage();
            if (syncSuccess) {

              try {
                await this.userService.setUserProfile();
              } catch (error) {
                console.warn('[AppComponent] Failed to fetch user profile after Android token sync on resume (possibly offline), using cached profile:', error);
                // Continue with cached profile from storage
              }

              return; // Skip normal refresh if we synced from native
            }
          }

          // Fallback: Normal token refresh - only if access token is expired
          const accessToken = await this.oktaAuthService.getOktaAuth().tokenManager.get('accessToken');
          const refreshToken = await this.oktaAuthService.getOktaAuth().tokenManager.get('refreshToken');

          // Only refresh if access token is missing or expired
          if (refreshToken && (!accessToken || this.oktaAuthService.getOktaAuth().tokenManager.hasExpired(accessToken))) {

            // Check network availability before attempting refresh
            const networkAvailable = await this.networkMonitor.isNetworkAvailable();
            if (!networkAvailable) {
              console.warn('[AppComponent] App resume - Network unavailable, skipping token refresh and allowing offline access');
              // Skip to offline access check below
            } else {
              // Bounded + raced + cancellable in-place refresh (mobile). Same 20s ceiling as cold
              // start, but it never navigates — the user is already on a real page. On timeout/
              // failure we drop the overlay and let them keep using the app with cached data.
              await this.refreshOnResumeWithDeadline(refreshToken);
            }
          } else if (accessToken && !this.oktaAuthService.getOktaAuth().tokenManager.hasExpired(accessToken)) {

            // Still set user profile in case it was cleared
            try {
              await this.userService.setUserProfile();
            } catch (error) {
              console.warn('[AppComponent] Failed to fetch user profile (possibly offline), using cached profile:', error);
              // Continue with cached profile from storage (loaded in UserService constructor)
            }
          }

          // Resume polling when app becomes active
          this.globalPollingService.resumePolling();
        } else {
          // Pause polling when app goes to background
          this.globalPollingService.pausePolling();
        }
      });

      // Mobile only: when connectivity returns while we're sitting on the offline/retry screen,
      // deterministically re-run the bounded cold-start refresh (rather than relying on a stale
      // in-flight request to eventually complete and navigate at a surprising moment).
      Network.addListener('networkStatusChange', (status) => {
        if (status.connected && this.authOfflineRetry) {
          console.warn('[AppComponent] Connectivity restored on offline/retry screen - re-running cold-start auth');
          this.zone.run(() => { this.retryColdStartAuth(); });
        }
      });
    }



    // if a critical update for mobile is avaliable, then we can't proceed - for web we dont need to check for updates
    if (!this.isCriticalUpdateAvaliable) {

      if (Capacitor.isNativePlatform()) {
        // Mobile owns a dedicated bounded cold-start auth flow: each refresh request is
        // timeout-bound (see okta-auth.service), the whole attempt is raced against a hard
        // ceiling, and failure lands on an explicit offline/retry screen instead of silently
        // dropping the user on the login page. Web keeps its original flow unchanged below.
        const mobileUrlParams = new URLSearchParams(window.location.search);
        const mobileHasOktaRedirect = mobileUrlParams.has('code') && mobileUrlParams.has('state');
        try {
          await this.handleMobileColdStartAuth(initialBrowserPath, mobileHasOktaRedirect);
        } catch (error) {
          console.error('[AppComponent] Mobile cold start auth failed unexpectedly:', error);
          this.zone.run(() => { this.isAuthenticating = false; });
        }
      } else {
        // get the accessToken and if it exists and is not expired, then we can proceed
        const accessToken = await this.oktaAuthService.getOktaAuth().tokenManager.get('accessToken') as AccessToken;

        const urlParams = new URLSearchParams(window.location.search);
        const hasOktaRedirect = urlParams.has('code') && urlParams.has('state');

        if (hasOktaRedirect) {
          // Returning from an Okta redirect (?code&state): LoginCallbackComponent owns the token
          // exchange and post-login navigation. Do NOT run cold-start refresh/logout here — it
          // races the callback, and if storage still holds a stale/expired token the failed
          // refresh clears tokens, wiping the freshly-issued ones out from under the completed
          // login and bouncing the user back to the login screen. Clean logins have no stored
          // token, so this only bit browser profiles where a stale token lingered (e.g. after
          // earlier dev logins). The overlay is hidden below; the callback navigates onward.
        } else if (accessToken && !this.oktaAuthService.getOktaAuth().tokenManager.hasExpired(accessToken)) {
          // Access token is valid - set user profile with current token

          try {
            await this.userService.setUserProfile();
          } catch (error) {
            console.warn('[AppComponent] Failed to fetch user profile on cold start (possibly offline), using cached profile:', error);
            // Continue with cached profile from storage - app will work offline with valid tokens
          }

          // Brief delay to ensure token manager is fully synced before navigation
          await new Promise(resolve => setTimeout(resolve, 50));

          // Log ResumeSession event (cold start with valid token) - skip if offline
          try {
            const rawDeviceInfo = await this.userService.getUserDeviceInfo();

            let formattedDeviceInfo: any = {
              isMobile: rawDeviceInfo.isMobile,
              timestamp: new Date().toISOString(),
              authType: 'ResumeSession'
            };

            if (rawDeviceInfo.isMobile) {
              formattedDeviceInfo.mobile = JSON.stringify(rawDeviceInfo);
            } else {
              formattedDeviceInfo.web = {
                userAgent: rawDeviceInfo.userAgent,
                platform: rawDeviceInfo.platform,
                screen: rawDeviceInfo.screen,
                cookieEnabled: rawDeviceInfo.cookieEnabled,
                onLine: rawDeviceInfo.onLine
              };
            }

            await this.userService.logUserDeviceInfo(formattedDeviceInfo);
          } catch (error) {
            console.error('[AppComponent] Error logging ResumeSession event:', error);
          }

          this.initializeDirectoryData();
          // Navigate to main menu ONLY if at root/login AND no Okta redirect or deep link pending
          const hasPendingDeepLink = await this.hybridStorageService.getItem<string>(HybridStorageKeys.Capacitor_DeepLinkPath);

          // For mobile: Also check if we're at /callback after Okta redirect
          const isAtAuthRoute = this.router.url.split('?')[0] === '/' || this.router.url.includes('/login') || this.router.url.includes('/callback');

          if (hasPendingDeepLink && !hasOktaRedirect) {
            await this.hybridStorageService.removeItem(HybridStorageKeys.Capacitor_DeepLinkPath);
            if (isAtAuthRoute) {
              // Router is still at an auth/root route — navigate to the stored deep link.
              // Only fire when the router hasn't resolved to the real page yet; if it already
              // has (isAtAuthRoute = false), Angular handled navigation and a second navigateByUrl
              // would re-run guards and potentially land on the wrong page.
              this.zone.run(() => { this.router.navigateByUrl(hasPendingDeepLink); });
            }
            // !isAtAuthRoute: router already navigated to the correct page — deep link cleared, done.
          } else if (isAtAuthRoute && !hasOktaRedirect) {
            // Use the URL captured at startup (before any async could change it via OktaAuthGuard).
            // If the browser was already on a real app page (e.g. HMR reload on /apps/posts/detail/123),
            // navigate back there. Only fall back to /mainmenu when starting from a true auth route.
            const isInitialPathAuthRoute = !initialBrowserPath
              || initialBrowserPath.split('?')[0] === '/'
              || initialBrowserPath.includes('/login')
              || initialBrowserPath.includes('/callback')
              || initialBrowserPath.includes('/logout');
            this.zone.run(() => {
              this.router.navigateByUrl(isInitialPathAuthRoute ? '/mainmenu' : initialBrowserPath);
            });
          }
        } else {
          // Access token expired or doesn't exist - check refresh token

          const refreshToken = await this.oktaAuthService.getOktaAuth().tokenManager.get('refreshToken');

          if (refreshToken) {

            let refreshSuccess = false;
            let successfulAttempt = 0;

            // Check network availability before attempting refresh
            const networkAvailable = await this.networkMonitor.isNetworkAvailable();
            if (networkAvailable) {

              // Get adaptive retry config based on network state
              const retryConfig = this.networkMonitor.getRetryConfig();
              const maxRetries = retryConfig.maxRetries;
              const initialDelay = retryConfig.initialDelay;

              for (let attempt = 1; attempt <= maxRetries; attempt++) {

                // Route through ensureFreshAccessToken (not a direct refresh): validity-first +
                // single-flight, so the web cold start can't double-spend a rotating refresh token
                // against a concurrent guard/interceptor refresh.
                refreshSuccess = await this.oktaAuthService.ensureFreshAccessToken(attempt === 1);

                if (refreshSuccess) {
                  successfulAttempt = attempt;
                  // Now set user profile with the NEW access token
                  try {
                    await this.userService.setUserProfile();
                  } catch (error) {
                    console.warn('[AppComponent] Failed to fetch user profile after token refresh on cold start (possibly offline), using cached profile:', error);
                    // Continue with cached profile from storage
                  }

                  // Brief delay to ensure token manager has fully synced tokens to storage before navigation
                  // This prevents race condition where OktaAuthGuard checks authentication before tokens are persisted
                  await new Promise(resolve => setTimeout(resolve, 100));

                  this.initializeDirectoryData();


                  // Navigate to main menu ONLY if at root/login AND no Okta redirect or deep link pending
                  const hasPendingDeepLink = await this.hybridStorageService.getItem<string>(HybridStorageKeys.Capacitor_DeepLinkPath);

                  // For mobile: Also check if we're at /callback after Okta redirect
                  const isAtAuthRoute = this.router.url.split('?')[0] === '/' || this.router.url.includes('/login') || this.router.url.includes('/callback');

                  if (hasPendingDeepLink && !hasOktaRedirect) {
                    await this.hybridStorageService.removeItem(HybridStorageKeys.Capacitor_DeepLinkPath);
                    if (isAtAuthRoute) {
                      // Router is still at an auth/root route — navigate to the stored deep link.
                      // Only fire when the router hasn't resolved to the real page yet; if it already
                      // has (isAtAuthRoute = false), Angular handled navigation and a second navigateByUrl
                      // would re-run guards and potentially land on the wrong page.
                      this.zone.run(() => { this.router.navigateByUrl(hasPendingDeepLink); });
                    }
                    // !isAtAuthRoute: router already navigated to the correct page — deep link cleared, done.
                  } else if (isAtAuthRoute && !hasOktaRedirect) {
                    const isInitialPathAuthRoute = !initialBrowserPath
                      || initialBrowserPath.split('?')[0] === '/'
                      || initialBrowserPath.includes('/login')
                      || initialBrowserPath.includes('/callback')
                      || initialBrowserPath.includes('/logout');
                    this.zone.run(() => {
                      this.router.navigateByUrl(isInitialPathAuthRoute ? '/mainmenu' : initialBrowserPath);
                    });
                  }

                  break; // Success - exit retry loop
                } else {

                  if (attempt < maxRetries) {
                    // Exponential backoff with configurable initial delay
                    const delay = initialDelay * Math.pow(2, attempt - 1);
                    const maxDelay = 8000; // Cap at 8 seconds per retry
                    const actualDelay = Math.min(delay, maxDelay);

                    console.warn(`[AppComponent] Cold start - Token refresh retry ${attempt}/${maxRetries} after ${actualDelay}ms`);
                    await new Promise(resolve => setTimeout(resolve, actualDelay));
                  }
                }
              }

              // Show diagnostics if debug mode enabled
              await this.showLoginDiagnostics('TokenRefresh-ColdStart', successfulAttempt || maxRetries, maxRetries, refreshSuccess, refreshToken, 'AppComponent.handleAppStart:478');
            } else {
              // Network unavailable at cold start - log message and fallback to offline access below
              console.warn('[AppComponent] Cold start - Network unavailable, skipping token refresh and allowing offline access');
            }

            if (!refreshSuccess) {
              // Refresh failed after retries - refreshAccessToken already handles logout for auth errors (401, 403, invalid_grant)
              // For network errors, tokens are preserved - check if we can allow offline access

              // Wait for cached profile to load (race condition fix for slow storage on tablets)
              await this.waitForCachedProfileLoad();

              // Check if we have a cached user profile to allow offline access
              const hasCachedProfile = !!this.userService.CurrentUserProfile;
              const isRefreshTokenValid = !this.oktaAuthService.getOktaAuth().tokenManager.hasExpired(refreshToken);

              if (hasCachedProfile && isRefreshTokenValid) {
                // Emit auth status as TRUE for offline mode (user has valid refresh token + cached profile)
                // Don't use emitAuthStatus() as it checks access token which is expired
                this.userService.emitAuthStatusValue(true);

                // Initialize directory data with cached values only (offline mode):
                // the access token is expired here, so skip authenticated fetches to
                // avoid a guaranteed 401 on GetSchoolsByUserIdAsync.
                this.initializeDirectoryData(true);

                // Navigate to main menu or deep link
                const hasPendingDeepLink = await this.hybridStorageService.getItem<string>(HybridStorageKeys.Capacitor_DeepLinkPath);
                const isAtAuthRoute = this.router.url.split('?')[0] === '/' || this.router.url.includes('/login') || this.router.url.includes('/callback');

                if (hasPendingDeepLink && !hasOktaRedirect) {
                  await this.hybridStorageService.removeItem(HybridStorageKeys.Capacitor_DeepLinkPath);
                  if (isAtAuthRoute) {
                    this.zone.run(() => { this.router.navigateByUrl(hasPendingDeepLink); });
                  }
                } else if (isAtAuthRoute && !hasOktaRedirect) {
                  const isInitialPathAuthRoute = !initialBrowserPath
                    || initialBrowserPath.split('?')[0] === '/'
                    || initialBrowserPath.includes('/login')
                    || initialBrowserPath.includes('/callback')
                    || initialBrowserPath.includes('/logout');
                  const targetRoute = isInitialPathAuthRoute ? '/mainmenu' : initialBrowserPath;
                  this.zone.run(() => { this.router.navigateByUrl(targetRoute); });
                }
              }
              // Edge case: No cached profile but valid refresh token
              // This can happen if profile cache was cleared but tokens remain in localStorage
              // In this rare case, let the app continue - user will need to re-authenticate or
              // the profile will be refetched on next successful API call

              // Hide overlay after failed refresh (mirrors app resume behavior)
              this.zone.run(() => {
                this.isAuthenticating = false;
              });
            }
          }
          // If no refresh token at all, user is not logged in - that's fine, they'll see login screen
        }

        // Web: auth is now resolved (valid token, successful refresh, offline-with-cache, or
        // logged-out). Hide the overlay immediately instead of waiting for the 2s/10s fallback
        // timers — those remain only as a safety net. waitForAuthResolution() is mobile-only
        // (it also drives Capgo), so web has no other success-driven hide.
        this.zone.run(() => { this.isAuthenticating = false; });
      } // end web-only cold-start auth (mobile handled above by handleMobileColdStartAuth)


      if (Capacitor.isNativePlatform()) {
        const urlParams = new URLSearchParams(window.location.search);
        const hasOktaRedirect = urlParams.has('code') && urlParams.has('state');

        this.hybridStorageService.removeItem(HybridStorageKeys.Capacitor_backgroundTimestamp);
        const isInitialized = await this.hybridStorageService.getItem<boolean>(HybridStorageKeys.Capacitor_stateChangeIsInitialized);

        if (isInitialized !== true) {

          await this.deepLinkService.initializeDeepLinkListener();
          await this.hybridStorageService.setItem(HybridStorageKeys.Capacitor_stateChangeIsInitialized, true);
        }

        if (!hasOktaRedirect) {

          // if it's a cold start and isInitialized is true, then we need to call this.deepLinkService.initializeDeepLinkListener() again
          // because the app was closed and opened again, and the listener was not initialized
          if (isInitialized === true) {
            this.deepLinkService.initializeDeepLinkListener(); // only initialize once
            // reset for the next cold start
            await this.hybridStorageService.setItem(HybridStorageKeys.Capacitor_stateChangeIsInitialized, false);
          }


          await this.handleAppStart();

          this.userService.authStatus$.subscribe(_ => {});
        } else {

        }
      }
    }
    this.detectPlatform();

    // Web only: hide overlay after a short delay if auth status hasn't resolved yet.
    // On mobile this timer is intentionally NOT used — it is exactly what caused the
    // "spinner disappears while the refresh is still running in the background" bug. The
    // mobile cold-start flow keeps the overlay up until auth truly resolves (see
    // handleMobileColdStartAuth) so the login screen never flashes mid-refresh.
    if (!Capacitor.isNativePlatform()) {
      setTimeout(() => {
        if (this.isAuthenticating) {
          console.warn('[AppComponent] Auth didn\'t resolve after ngOnInit - hiding overlay as fallback');
          this.isAuthenticating = false;
        }
      }, 2000);
    }
  }

  async ngAfterViewInit() {
    // Set up the bottom sheet container after view initialization
    this.bottomSheetService.setContainer(this.bottomSheetContainer);

    // Lazy load AI widget when user profile is available and not on auth screens
    const navigationEnd$ = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    );

    combineLatest([
      this.userService.currentUserProfile$,
      navigationEnd$
    ]).subscribe(async ([profile]) => {
      const currentUrl = this.router.url;
      const authRoutes = ['/', '/login', '/logout', '/loggedout', '/callback'];
      const isOnAuthScreen = authRoutes.some(route =>
        currentUrl === route || currentUrl.startsWith(route + '?')
      );

      const shouldShowWidget = profile?.ShowAI && profile.UserType != "Student" && this.aiWidgetContainer && !isOnAuthScreen;

      // In ngAfterViewInit, change the widget creation logic:
      if (shouldShowWidget) {
        // Prevent race condition - don't create if already creating or created
        if (!this.aiWidgetCreated && !this.isCreatingWidget) {
          this.isCreatingWidget = true; // Lock immediately
          this.aiWidgetContainer.clear();
          const { AiChatWidgetComponent } = await import('./apps/shared/ai/ai-chat-widget/ai-chat-widget.component');
          this.aiWidgetContainer.createComponent(AiChatWidgetComponent);
          this.aiWidgetCreated = true;
          this.isCreatingWidget = false; // Unlock after creation
        }
      } else {
        if (this.aiWidgetCreated) {
          this.aiWidgetContainer.clear();
          this.aiWidgetCreated = false;
        }
      }
    });
  }

  async handleAppStart() {

    // If in demo mode, skip token refresh and force fresh login
    if (this.isDemoMode) {

      try {
        this.oktaAuthService.getOktaAuth().tokenManager.clear();
      } catch (e) {
        console.error('[AppComponent] Error clearing tokens:', e);
      }
      return;
    }

    // Note: Token refresh is now handled in ngOnInit before this method is called
    // This method is kept for compatibility but the main refresh logic has moved up
    // to ensure user profile is set immediately after token refresh
  }

  // ===========================================================================================
  // Mobile (native) cold-start authentication
  //
  // Replaces the web inline flow for native platforms only. Goals:
  //  - the loading overlay stays up until auth TRULY resolves (no more "spinner gone while the
  //    refresh is still running invisibly", which made the app jump to /mainmenu minutes later)
  //  - each refresh attempt is timeout-bound (okta-auth.service) and the whole attempt is raced
  //    against a hard ceiling that cancels the work, so we can't hang forever either
  //  - on failure with a still-valid refresh token we show an explicit offline/retry screen and
  //    auto-recover when connectivity returns, instead of dumping the user on the login page
  // ===========================================================================================
  private async handleMobileColdStartAuth(initialBrowserPath: string, hasOktaRedirect: boolean): Promise<void> {
    // Remember navigation context for the reconnect listener / manual retry button.
    this.coldStartInitialPath = initialBrowserPath;
    this.coldStartHasOktaRedirect = hasOktaRedirect;

    // Pick up any tokens a background worker refreshed while the app was closed.
    await this.syncNativeTokens();

    const oktaAuth = this.oktaAuthService.getOktaAuth();
    const accessToken = await oktaAuth.tokenManager.get('accessToken') as AccessToken;

    // Valid access token already in hand → resume the session and navigate immediately.
    if (accessToken && !oktaAuth.tokenManager.hasExpired(accessToken)) {
      try {
        await this.userService.setUserProfile();
      } catch (error) {
        console.warn('[AppComponent] Failed to fetch user profile on cold start (possibly offline), using cached profile:', error);
      }
      await new Promise(resolve => setTimeout(resolve, 50));
      await this.logResumeSession();
      this.initializeDirectoryData();
      await this.navigateAfterColdStartAuth(initialBrowserPath, hasOktaRedirect);
      this.zone.run(() => { this.isAuthenticating = false; });
      return;
    }

    // Access token expired/missing → need a refresh.
    const refreshToken = await oktaAuth.tokenManager.get('refreshToken');
    if (!refreshToken) {
      // No tokens at all → genuinely logged out. Drop the overlay so the login screen shows.
      this.zone.run(() => { this.isAuthenticating = false; });
      return;
    }

    const result = await this.runBoundedRefreshWithDeadline(refreshToken);
    if (result === 'success') {
      // Navigation + overlay hide already handled inside the loop.
      return;
    }

    // 'failed' or 'timeout' → terminal offline handling (cached-data access or retry screen).
    await this.enterOfflineRetryState();
  }

  /** iOS Keychain / Android native-storage token sync, refreshing the cached profile if it changed. */
  private async syncNativeTokens(): Promise<void> {
    const platform = Capacitor.getPlatform();
    let synced = false;
    if (platform === 'ios') {
      synced = await this.oktaAuthService.syncTokensFromKeychain();
    } else if (platform === 'android') {
      synced = await this.oktaAuthService.syncTokensFromNativeStorage();
    }
    if (synced) {
      try {
        await this.userService.setUserProfile();
      } catch (error) {
        console.warn('[AppComponent] Failed to fetch user profile after native token sync (possibly offline), using cached profile:', error);
      }
    }
  }

  /** Log a ResumeSession device event (cold start with a still-valid access token). */
  private async logResumeSession(): Promise<void> {
    try {
      const rawDeviceInfo = await this.userService.getUserDeviceInfo();
      const formattedDeviceInfo: any = {
        isMobile: rawDeviceInfo.isMobile,
        timestamp: new Date().toISOString(),
        authType: 'ResumeSession'
      };
      if (rawDeviceInfo.isMobile) {
        formattedDeviceInfo.mobile = JSON.stringify(rawDeviceInfo);
      } else {
        formattedDeviceInfo.web = {
          userAgent: rawDeviceInfo.userAgent,
          platform: rawDeviceInfo.platform,
          screen: rawDeviceInfo.screen,
          cookieEnabled: rawDeviceInfo.cookieEnabled,
          onLine: rawDeviceInfo.onLine
        };
      }
      await this.userService.logUserDeviceInfo(formattedDeviceInfo);
    } catch (error) {
      console.error('[AppComponent] Error logging ResumeSession event:', error);
    }
  }

  /**
   * Run the bounded refresh loop, raced against a hard ceiling. If the ceiling wins, the loop is
   * cancelled so a late-completing request cannot navigate the user unexpectedly. Returns
   * 'success' once a refresh succeeds and navigation has run; 'failed' otherwise (incl. timeout).
   */
  private async runBoundedRefreshWithDeadline(refreshToken: any): Promise<'success' | 'failed'> {
    const token = { cancelled: false };

    const deadline = new Promise<'timeout'>(resolve =>
      setTimeout(() => resolve('timeout'), AppComponent.MOBILE_COLD_START_CEILING_MS)
    );

    const outcome = await Promise.race([this.refreshLoop(refreshToken, token), deadline]);

    if (outcome === 'timeout') {
      console.warn('[AppComponent] Mobile cold-start refresh hit hard ceiling - cancelling and showing offline/retry');
      token.cancelled = true; // stop the loop from navigating on a late success
      return 'failed';
    }
    return outcome;
  }

  /**
   * Bounded retry loop. Each attempt's HTTP request is already timeout-bound in okta-auth.service.
   * Checks the cancellation token before refreshing and before navigating so it stops cleanly when
   * the deadline fires.
   */
  private async refreshLoop(refreshToken: any, token: { cancelled: boolean }): Promise<'success' | 'failed'> {
    // If we're flatly offline, don't hammer the network — go straight to the offline/retry state.
    const networkAvailable = await this.networkMonitor.isNetworkAvailable();
    if (!networkAvailable) {
      console.warn('[AppComponent] Mobile cold start - network unavailable, skipping refresh');
      return 'failed';
    }

    const { maxRetries, initialDelay } = this.networkMonitor.getRetryConfig();

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      if (token.cancelled) return 'failed';

      // Go through ensureFreshAccessToken (not refreshAccessToken directly): it short-circuits if
      // the token is already fresh (e.g. an interceptor call refreshed it first) and shares the
      // single-flight refresh, so this loop can't double-spend a rotating refresh token.
      const refreshSuccess = await this.oktaAuthService.ensureFreshAccessToken(attempt === 1);

      if (token.cancelled) return 'failed'; // gave up while this request was in flight

      if (refreshSuccess) {
        try {
          await this.userService.setUserProfile();
        } catch (error) {
          console.warn('[AppComponent] Failed to fetch user profile after token refresh on cold start (possibly offline), using cached profile:', error);
        }
        // Brief delay so token-manager writes flush before guards re-check on navigation.
        await new Promise(resolve => setTimeout(resolve, 100));
        this.initializeDirectoryData();
        await this.navigateAfterColdStartAuth(this.coldStartInitialPath, this.coldStartHasOktaRedirect);
        this.zone.run(() => { this.isAuthenticating = false; this.authOfflineRetry = false; });
        await this.showLoginDiagnostics('TokenRefresh-ColdStart', attempt, maxRetries, true, refreshToken, 'AppComponent.refreshLoop');
        return 'success';
      }

      if (attempt < maxRetries && !token.cancelled) {
        const delay = Math.min(initialDelay * Math.pow(2, attempt - 1), 8000);
        console.warn(`[AppComponent] Mobile cold start - token refresh retry ${attempt}/${maxRetries} after ${delay}ms`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }

    await this.showLoginDiagnostics('TokenRefresh-ColdStart', maxRetries, maxRetries, false, refreshToken, 'AppComponent.refreshLoop');
    return 'failed';
  }

  /**
   * Refresh failed (offline / weak signal). Prefer cached-data offline access when we have a cached
   * profile + valid refresh token; otherwise surface an explicit offline/retry screen (instead of
   * the login page) as long as the refresh token is still valid. A truly expired refresh token
   * falls through to the login screen.
   */
  private async enterOfflineRetryState(): Promise<void> {
    // Okta rejected the session. Its logout may be stalled with the dead tokens still in storage, so
    // neither the cached-data fallback nor Retry can work — tell the user to sign in again instead.
    if (this.oktaAuthService.sessionRejected) {
      this.zone.run(() => {
        this.isAuthenticating = false;
        this.authOfflineRetry = false;
        this.authSessionExpired = true;
      });
      return;
    }

    await this.waitForCachedProfileLoad();
    const hasCachedProfile = !!this.userService.CurrentUserProfile;

    // Re-read the refresh token instead of trusting the one captured before the refresh attempt: an
    // invalid_grant during the attempt logs out and clears storage, but the captured object still
    // carries a future expiresAt — which would park the user on a bogus "Can't reach the network"
    // screen instead of the login page.
    const oktaAuth = this.oktaAuthService.getOktaAuth();
    const currentRefreshToken = await oktaAuth.tokenManager.get('refreshToken');
    const isRefreshTokenValid = !!currentRefreshToken && !oktaAuth.tokenManager.hasExpired(currentRefreshToken);

    if (hasCachedProfile && isRefreshTokenValid) {
      // Let the user keep using the app with cached data; a future API call / reconnect refreshes.
      this.userService.emitAuthStatusValue(true);
      this.initializeDirectoryData();
      await this.navigateAfterColdStartAuth(this.coldStartInitialPath, this.coldStartHasOktaRedirect);
      this.zone.run(() => { this.isAuthenticating = false; this.authOfflineRetry = false; });
      return;
    }

    // No cached data to fall back on. If the refresh token is still valid, show the offline/retry
    // screen and auto-recover on reconnect; otherwise reveal the login screen.
    this.zone.run(() => {
      this.isAuthenticating = false;
      this.authOfflineRetry = isRefreshTokenValid;
    });
  }

  /**
   * Re-run the bounded cold-start refresh. Invoked by the Retry button and by the network
   * reconnect listener. No-op while a refresh is already in flight.
   */
  async retryColdStartAuth(): Promise<void> {
    if (this.isAuthenticating) return;

    const oktaAuth = this.oktaAuthService.getOktaAuth();
    const refreshToken = await oktaAuth.tokenManager.get('refreshToken');
    if (!refreshToken || oktaAuth.tokenManager.hasExpired(refreshToken)) {
      // Nothing to recover with → send the user to the login screen.
      this.zone.run(() => { this.authOfflineRetry = false; this.isAuthenticating = false; });
      return;
    }

    this.zone.run(() => { this.authOfflineRetry = false; this.isAuthenticating = true; });
    const result = await this.runBoundedRefreshWithDeadline(refreshToken);
    if (result !== 'success') {
      await this.enterOfflineRetryState();
    }
  }

  /**
   * Escape hatch from the offline/retry screen when the stored session can't be recovered (e.g.
   * stale tokens the refresh keeps rejecting without invalid_grant, so Retry loops forever).
   * clearUnusableSession also wipes the Keychain / native copies and sets the logged-out flag, so
   * the next cold start can't restore them. The Okta SSO session is left intact, so sign-in may
   * complete silently.
   */
  async signInAgain(): Promise<void> {
    if (this.isSigningInAgain) return;
    this.isSigningInAgain = true;
    try {
      await this.oktaAuthService.clearUnusableSession('User chose Sign in again on offline/retry screen');
    } catch (error) {
      console.error('[AppComponent] Failed to clear session for Sign in again:', error);
    } finally {
      this.zone.run(() => {
        this.authOfflineRetry = false;
        this.authSessionExpired = false;
        this.isAuthenticating = false;
        this.isSigningInAgain = false;
      });
      await this.router.navigate(['']);
    }
  }

  /**
   * Shared post-auth navigation used by every mobile cold-start outcome. Mirrors the web inline
   * logic: honor a pending deep link, else navigate to the captured initial path (or /mainmenu).
   */
  private async navigateAfterColdStartAuth(initialBrowserPath: string, hasOktaRedirect: boolean): Promise<void> {
    // A short-link deep link is being resolved + navigated by DeepLinkService. Stay out of its way —
    // otherwise we race it to /mainmenu and usually win (no network needed), dropping the user on
    // the home screen instead of the linked content. DeepLinkService owns this navigation.
    if (this.deepLinkService.deepLinkPending) {
      return;
    }
    const hasPendingDeepLink = await this.hybridStorageService.getItem<string>(HybridStorageKeys.Capacitor_DeepLinkPath);
    const isAtAuthRoute = this.router.url.split('?')[0] === '/' || this.router.url.includes('/login') || this.router.url.includes('/callback');

    if (hasPendingDeepLink && !hasOktaRedirect) {
      await this.hybridStorageService.removeItem(HybridStorageKeys.Capacitor_DeepLinkPath);
      if (isAtAuthRoute) {
        this.zone.run(() => { this.router.navigateByUrl(hasPendingDeepLink); });
      }
    } else if (isAtAuthRoute && !hasOktaRedirect) {
      const isInitialPathAuthRoute = !initialBrowserPath
        || initialBrowserPath.split('?')[0] === '/'
        || initialBrowserPath.includes('/login')
        || initialBrowserPath.includes('/callback')
        || initialBrowserPath.includes('/logout');
      this.zone.run(() => {
        this.router.navigateByUrl(isInitialPathAuthRoute ? '/mainmenu' : initialBrowserPath);
      });
    }
  }

  /**
   * App-resume token refresh (mobile). Same bounded + raced + cancellable shape as cold start with
   * the same 20s ceiling, but it refreshes the token IN PLACE and never navigates — the user is
   * already on a real page. On timeout/failure we drop the blocking overlay and let them continue
   * with cached data; a later API call / the next resume completes the refresh. This stops the
   * spinner from camping on the user's current screen for 35–70s on a weak connection.
   */
  private async refreshOnResumeWithDeadline(refreshToken: any): Promise<void> {
    // Show overlay to prevent "blip" UX issue
    this.zone.run(() => { this.isAuthenticating = true; });

    const token = { cancelled: false };
    const stats = { attempts: 0, maxRetries: 0 };

    const deadline = new Promise<'timeout'>(resolve =>
      setTimeout(() => resolve('timeout'), AppComponent.MOBILE_COLD_START_CEILING_MS)
    );

    const outcome = await Promise.race([this.resumeRefreshLoop(token, stats), deadline]);

    if (outcome === 'timeout') {
      console.warn('[AppComponent] App resume - token refresh hit hard ceiling - cancelling, continuing with cached data');
      token.cancelled = true; // stop the loop from retrying after we drop the overlay
    }

    // Hide the blocking overlay regardless of outcome.
    this.zone.run(() => { this.isAuthenticating = false; });

    const refreshSuccess = outcome === 'success';
    await this.showLoginDiagnostics('TokenRefresh-AppResume', stats.attempts || stats.maxRetries, stats.maxRetries, refreshSuccess, refreshToken, 'AppComponent.refreshOnResumeWithDeadline');

    if (!refreshSuccess) {
      // Allow offline access with cached data. refreshAccessToken already logs out on auth errors
      // (401/403/invalid_grant); for network errors tokens are preserved and the user stays in the
      // app. Nothing to navigate — they're already on a page.
      await this.waitForCachedProfileLoad();
    }
  }

  /**
   * In-place resume refresh loop: timeout-bound requests, exponential backoff, cancellation-aware.
   * Refreshes the token and the cached profile but does NOT navigate. Reports attempt counts via
   * `stats` for the diagnostics modal.
   */
  private async resumeRefreshLoop(
    token: { cancelled: boolean },
    stats: { attempts: number; maxRetries: number }
  ): Promise<'success' | 'failed'> {
    const { maxRetries, initialDelay } = this.networkMonitor.getRetryConfig();
    stats.maxRetries = maxRetries;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      if (token.cancelled) return 'failed';
      stats.attempts = attempt;

      // Single-flight + validity-first (see ensureFreshAccessToken) so the resume refresh can't
      // double-spend a rotating refresh token against a concurrent interceptor/guard refresh.
      const refreshSuccess = await this.oktaAuthService.ensureFreshAccessToken(attempt === 1);

      if (token.cancelled) return 'failed';

      if (refreshSuccess) {
        try {
          await this.userService.setUserProfile();
        } catch (error) {
          console.warn('[AppComponent] Failed to fetch user profile after token refresh on app resume (possibly offline), using cached profile:', error);
        }
        return 'success';
      }

      if (attempt < maxRetries && !token.cancelled) {
        const delay = Math.min(initialDelay * Math.pow(2, attempt - 1), 8000);
        console.warn(`[AppComponent] App resume - token refresh retry ${attempt}/${maxRetries} after ${delay}ms`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    return 'failed';
  }

  // Detect platform for styling
  detectPlatform() {
    const isIOS = Capacitor.getPlatform() === 'ios';
    const isAndroid = Capacitor.getPlatform() === 'android';
    const isWeb = Capacitor.getPlatform() === 'web';

    if (isIOS) {
      document.body.classList.add('ios');
    } else if (isAndroid) {
      document.body.classList.add('android');
    } else if (isWeb) {
      this.detectWebPlatform();
    }
  }

  detectWebPlatform() {
    // detect if we're web mobile
    const us = navigator.userAgent.toLowerCase();
    const isIOS = us.includes('iphone') || us.includes('ipad');
    const isAndroid = us.includes('android');

    if (isIOS) {
      document.body.classList.add('ios');
    } else if (isAndroid) {
      document.body.classList.add('android');
    } 
  }

  showAI() {
    return this.userService.CurrentUserProfile?.ShowAI;
  }

  private initCapgo() {
    // Only run on native platforms
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    // Auth state resolved (logged in OR logged out)
    // We wait for handleAppStart to complete, which resolves auth state
    this.waitForAuthResolution();

    // First stable navigation finished - notify Capgo immediately after navigation
    // This prevents Capgo rollback timeout (10s) when users are stuck at login screen
    this.router.events
      .pipe(
        filter(e => e instanceof NavigationEnd),
        take(1)
      )
      .subscribe(() => {
        this.navReady = true;
        this.tryNotifyCapgo();
      });
  }

  private waitForAuthResolution() {
    // Auth state resolved (logged in OR logged out)
    // Subscribe to UserService authStatus$ which emits when auth state is determined
    this.userService.authStatus$
      .pipe(
        filter(isAuthenticated => isAuthenticated !== undefined),
        take(1)
      )
      .subscribe(() => {

        this.authReady = true;
        this.isAuthenticating = false; // Hide loading overlay once auth is resolved
        this.tryNotifyCapgo();
      });
  }

  private tryNotifyCapgo() {
    // Call notifyAppReady() as soon as:
    // 1. First navigation completes (navReady = true), OR
    // 2. Auth completes (authReady = true)
    // Whichever happens first prevents Capgo rollback timeout
    if (this.authReady || this.navReady) {
      this.capgoReady.notifyOnce();
    }
  }

  /**
   * Wait for cached user profile to load from storage
   * This prevents race condition where offline check happens before profile loads
   * Especially important on tablets with slower storage I/O
   */
  private async waitForCachedProfileLoad(): Promise<void> {
    // If profile already loaded, return immediately
    if (this.userService.CurrentUserProfile) {
      return;
    }

    // Wait for profile to load via currentUserProfile$ observable
    // Timeout after 2 seconds to avoid blocking forever if no profile exists
    return new Promise<void>((resolve) => {
      const timeout = setTimeout(() => {
        resolve();
      }, 2000);

      const subscription = this.userService.currentUserProfile$.subscribe(profile => {
        if (profile) {
          clearTimeout(timeout);
          subscription.unsubscribe();
          resolve();
        }
      });
    });
  }

  /**
   * Show login diagnostics modal if debug mode is enabled
   */
  private async showLoginDiagnostics(
    authType: string,
    retriesUsed: number,
    maxRetries: number,
    success: boolean,
    refreshToken: any,
    callSite?: string
  ): Promise<void> {
    // Only show if debug mode is enabled
    if (!this.loginDebugService.isDebugModeEnabled) {
      return;
    }

    try {
      // Get device info
      const deviceInfo = await Device.getInfo();

      // Get network info
      const networkType = await this.networkMonitor.getNetworkType();
      const networkAvailable = await this.networkMonitor.isNetworkAvailable();
      const networkSwitching = this.networkMonitor.isCurrentlySwitching();

      // Build diagnostics object
      const diagnostics: LoginDiagnostics = {
        timestamp: new Date().toISOString(),
        authType: authType,
        callSite: callSite,
        networkType: networkType,
        networkAvailable: networkAvailable,
        networkSwitching: networkSwitching,
        retriesUsed: retriesUsed,
        maxRetries: maxRetries,
        success: success,
        platform: deviceInfo.platform,
        osVersion: deviceInfo.osVersion,
        modelName: deviceInfo.model,
        hadCachedProfile: !!this.userService.CurrentUserProfile,
        refreshTokenValid: !this.oktaAuthService.getOktaAuth().tokenManager.hasExpired(refreshToken)
      };

      // Show modal
      this.matDialog.open(LoginDiagnosticsModalComponent, {
        width: '600px',
        maxWidth: '90vw',
        data: diagnostics
      });

    } catch (error) {
      console.error('[LoginDiagnostics] Failed to show diagnostics:', error);
    }
  }

  /**
   * Initialize directory data on app load:
   * 1. Load schools for user (if not cached)
   * 2. Set selected school (validates existing selection)
   * 3. Refresh directory cache if older than 60 minutes (background)
   */
  private initializeDirectoryData(offlineMode: boolean = false): void {
    this.schoolsService.initializeDirectoryData(offlineMode);
  }

  /**
   * On mobile, a left-edge swipe (like native back gesture) navigates back.
   * If the page has an app-page-header with a back button, it triggers that.
   * Otherwise navigates to /mainmenu.
   */
  private setupSwipeBackNavigation(): void {
    if (!Capacitor.isNativePlatform()) return;

    const EDGE_THRESHOLD = 40;   // px from left edge to start swipe
    const MIN_SWIPE_X = 60;      // minimum horizontal distance
    const MAX_ANGLE_RATIO = 0.6; // deltaY / deltaX must be less than this (keep it mostly horizontal)

    let touchStartX = 0;
    let touchStartY = 0;
    let isEdgeSwipe = false;

    document.addEventListener('touchstart', (e: TouchEvent) => {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
      isEdgeSwipe = touchStartX <= EDGE_THRESHOLD;
    }, { passive: true });

    document.addEventListener('touchend', (e: TouchEvent) => {
      if (!isEdgeSwipe) return;

      const deltaX = e.changedTouches[0].clientX - touchStartX;
      const deltaY = Math.abs(e.changedTouches[0].clientY - touchStartY);

      if (deltaX >= MIN_SWIPE_X && deltaY / deltaX < MAX_ANGLE_RATIO) {
        const backBtn = document.querySelector<HTMLElement>('.page-header__back-button-wrapper');
        if (backBtn) {
          backBtn.click();
        } else {
          this.zone.run(() => this.router.navigateByUrl('/mainmenu'));
        }
      }

      isEdgeSwipe = false;
    }, { passive: true });
  }

  /**
   * On mobile, a downward swipe anywhere on screen dismisses the keyboard.
   * Works on both iOS and Android via Capacitor's Keyboard.hide().
   */
  private setupSwipeDownToDismissKeyboard(): void {
    if (!Capacitor.isNativePlatform()) return;

    let touchStartY = 0;
    const SWIPE_THRESHOLD = 50;

    document.addEventListener('touchstart', (e: TouchEvent) => {
      touchStartY = e.touches[0].clientY;
    }, { passive: true });

    document.addEventListener('touchend', (e: TouchEvent) => {
      const deltaY = e.changedTouches[0].clientY - touchStartY;
      if (deltaY > SWIPE_THRESHOLD && document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
        Keyboard.hide();
      }
    }, { passive: true });
  }

  /**
   * Intercept clicks on tel: links: log analytics, and on desktop web (where there's
   * typically no registered tel: handler) fall back to copying the number to the clipboard.
   */
  private setupPhoneClickTracking(): void {
    document.addEventListener('click', (event: MouseEvent) => {
      const link = (event.target as HTMLElement).closest('a[href^="tel:"]') as HTMLAnchorElement | null;
      if (!link) return;

      const phoneNumber = link.href.replace('tel:', '');
      const calledUserIdAttr = link.dataset['calledUserId'];
      const calledUserId = calledUserIdAttr ? Number(calledUserIdAttr) : undefined;
      this.analyticsService.logPhoneClicked(phoneNumber, calledUserId).subscribe();

      if (this.isDesktopWeb()) {
        event.preventDefault();
        this.copyPhoneToClipboard(phoneNumber);
      }
    }, true);
  }

  private isDesktopWeb(): boolean {
    if (Capacitor.isNativePlatform()) return false;
    return !/Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  }

  private copyPhoneToClipboard(phoneNumber: string): void {
    const showToast = () => this.snackBar.open(`Phone Number Copied: ${phoneNumber}`, undefined, { duration: 3000 });
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(phoneNumber).then(showToast).catch(err => {
        console.error('Failed to copy phone number:', err);
      });
    } else {
      showToast();
    }
  }

  /**
   * Close all open modals and bottom sheets when navigation occurs (e.g., footer menu clicked)
   */
  private setupGlobalModalClose(): void {
    // On mobile, intercept touch/tap events to catch footer navigation attempts
    if (Capacitor.isNativePlatform()) {
      let lastEventTime = 0;
      const DEBOUNCE_MS = 100; // Prevent multiple rapid closes

      const handleTouchOrClick = (event: TouchEvent | MouseEvent) => {
        const now = Date.now();
        if (now - lastEventTime < DEBOUNCE_MS) {
          return; // Debounce rapid events
        }

        if (this.matDialog.openDialogs.length === 0) {
          return; // No modals to close
        }

        // Get the target element from touch or mouse event
        let target: HTMLElement;
        if (event instanceof TouchEvent) {
          const touch = event.touches[0] || event.changedTouches[0];
          target = document.elementFromPoint(touch.clientX, touch.clientY) as HTMLElement;
        } else {
          target = event.target as HTMLElement;
        }

        if (!target) return;

        // Check if tapping on footer - this is a navigation intent, always close modals
        const isFooterOrNav = target.closest('app-footer, footer, nav, [role="navigation"]')
                           || target.closest('a[routerlink], a[href^="/"], button[routerlink]');

        if (isFooterOrNav) {
          // Footer tap detected - close ALL modals immediately
          lastEventTime = now;

          // Close all Material dialogs
          this.matDialog.closeAll();

          // Close all bottom sheets
          this.bottomSheetService.closeAll();

          // Let navigation happen
          return;
        }

        // Check if touch/click is outside modal content (on backdrop)
        // Also include Material CDK overlays (mat-select panels, autocomplete, etc.)
        const isInsideModal = target.closest('.mat-mdc-dialog-container, .mat-dialog-container, .cdk-overlay-container, .mat-mdc-select-panel, .mat-select-panel, .mat-autocomplete-panel');

        // Close modals if tapping backdrop
        if (!isInsideModal) {
          lastEventTime = now;

          // Close all Material dialogs
          this.matDialog.closeAll();

          // Close all bottom sheets
          this.bottomSheetService.closeAll();

          // NOTE: deliberately no stopPropagation() here. Both listeners below are
          // registered in the CAPTURE phase at document, so stopping propagation
          // prevents the event from ever reaching its target - it silently kills
          // every tap on the page while a modal is open.
          // Closing the modals is the intent; swallowing the tap is not.
        }
      };

      // Listen to touchstart for immediate response on mobile
      document.addEventListener('touchstart', handleTouchOrClick, true);

      // Also listen to click as fallback for compatibility
      document.addEventListener('click', handleTouchOrClick, true);
  
      // Also listen to navigation events as fallback
      this.router.events
        .pipe(filter(event => event instanceof NavigationStart))
        .subscribe(() => {
          // Close Material dialogs
          this.matDialog.closeAll();

          // Close bottom sheets
          this.bottomSheetService.closeAll();
        });
      }
  }
}
